import { useState } from 'react';
import StepCarousel from './StepCarousel';
import StepAccount from './StepAccount';
import StepLocation from './StepLocation';
import StepOptionalProfile from './StepOptionalProfile';
import StepVehicle from './StepVehicle';
import StepContext from './StepContext';
import StepPhoto from './StepPhoto';
import { saveVehicle, addGarage, getGarages, getVehicles, saveVehicles, saveGarages, hasUserProfile, saveUserProfile, acceptLegalDocument, getUserProfile, rekeyAnalysis, getSubscriptionLimits, getOwnedVehicleCount } from '../../utils/storage';
import { displayToMiles } from '../../utils/units';
import { LEGAL_VERSION } from '../LegalModal';
import { useAuth } from '../../contexts/AuthContext';
import { createUserProfile, createGarage as supaCreateGarage, createVehicle, loadUserGarages } from '../../services/userService';
import { reliableWrite } from '../../services/writeQueue';

/** Normalize phone input to E.164 format (+1XXXXXXXXXX) */
function normalizePhone(value) {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `+${digits}`;
}

/*
  Onboarding flow:
  - First-time users: 7 screens (Carousel → Account → Location → Optional → Vehicle sub-flow)
  - Returning users (adding a vehicle): 3 screens (Vehicle sub-flow only)

  The vehicle sub-flow has its own internal 3-step progression:
    Step V1: StepVehicle (year/make/model/mileage + TMU)
    Step V2: StepContext (factory-new bypass, service history, skill/use/condition)
    Step V3: StepPhoto (receipt photo + service notes)
*/

const PROFILE_STEPS = 4; // carousel, account, location, optional
const VEHICLE_STEPS = 3; // vehicle, context, photo

export default function Onboarding({ garages, targetGarageId, onComplete, onCancel }) {
  const { user, linkEmail, linkPhone } = useAuth();

  // BUG FIX: Lock isFirstTime at mount so it doesn't flip mid-flow
  // when saveUserProfile() is called at step 4 transition.
  const [isFirstTime] = useState(() => !hasUserProfile());
  const [submitting, setSubmitting] = useState(false);
  const totalSteps = isFirstTime ? PROFILE_STEPS + VEHICLE_STEPS : VEHICLE_STEPS;

  // Step: 1-based. First-time: 1-7 (4 profile + 3 vehicle). Returning: 1-3 (vehicle only).
  const [step, setStep] = useState(1);

  // Profile data (screens 1-4, first-time only)
  // Email comes from authenticated user, not from form input
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    phone: '',           // Collected when user signed in with email
    secondaryEmail: '',  // Collected when user signed in with phone
    zipCode: '',
    mechanicalLevel: '',
    yearOfBirth: '',
    salaryRange: '',
    primaryUseCases: [],
    referralSource: '',
  });

  // Vehicle data (screens 5-7 first-time, or 1-3 returning)
  const [vehicleData, setVehicleData] = useState({
    year: '',
    make: '',
    model: '',
    mileage: '',
    tmu: false,
    distance_units: '',
    lastService: '',
    lastServiceDescription: '',
    isFactoryNew: false,
    primaryUse: '',
    condition: '',
    vin: '',
    receiptPhotos: [],
    serviceHistory: '',
  });

  function updateProfile(fields) {
    setProfileData((prev) => ({ ...prev, ...fields }));
  }

  function updateVehicle(fields) {
    setVehicleData((prev) => ({ ...prev, ...fields }));
  }

  function next() {
    // When finishing profile step 4, save the profile + legal acceptances
    if (isFirstTime && step === PROFILE_STEPS) {
      // Include auth email/phone in localStorage profile
      const profileWithAuth = {
        ...profileData,
        email: user?.email || profileData.secondaryEmail || '',
        phone: profileData.phone || user?.phone || '',
      };
      saveUserProfile(profileWithAuth);
      acceptLegalDocument('terms', LEGAL_VERSION);
      acceptLegalDocument('privacy', LEGAL_VERSION);
      // Supabase profile save happens in finish() alongside garage + vehicle
    }
    setStep((s) => Math.min(s + 1, totalSteps));
  }

  function back() {
    setStep((s) => Math.max(s - 1, 1));
  }

  async function finish() {
    // Guard against double-submit (e.g. double-tap on finish button)
    if (submitting) return;
    setSubmitting(true);

    // Secondary vehicle limit check (primary is in App.jsx handleAddVehicleToGarage)
    if (!isFirstTime) {
      const limits = getSubscriptionLimits();
      // Only count OWNED vehicles (exclude shared/collaborator garage vehicles)
      if (getOwnedVehicleCount() >= limits.maxVehicles) {
        console.warn('[PM-Onboarding] Vehicle limit reached, aborting finish');
        setSubmitting(false);
        return;
      }
    }

    // Determine garage ID for localStorage
    let garageId = targetGarageId;
    if (!garageId) {
      const currentGarages = garages && garages.length > 0 ? garages : getGarages();
      if (currentGarages.length > 0) {
        garageId = currentGarages[0].id;
      } else {
        const newGarage = addGarage({ name: 'My Garage' });
        garageId = newGarage.id;
      }
    }

    const vehicleId = `vehicle_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    // Auto-populate skillLevel from user profile's mechanicalLevel
    const profile = getUserProfile();
    const skillLevel = vehicleData.skillLevel || profileData.mechanicalLevel || profile?.mechanicalLevel || 'Intermediate';
    // Convert mileage from display units (mi or km) to internal miles for storage
    // Use per-vehicle distance_units if set, otherwise falls back to global setting
    const storedMileage = vehicleData.mileage ? displayToMiles(parseInt(vehicleData.mileage, 10), vehicleData.distance_units || undefined) : vehicleData.mileage;
    const finalVehicle = { ...vehicleData, id: vehicleId, garageId, skillLevel, mileage: storedMileage };

    // Save to localStorage (source of truth) — strip receiptPhotos to avoid bloating storage
    // The photos are only needed for the initial GPT analysis and live in finalVehicle (in memory)
    const { receiptPhotos: _photos, ...vehicleForStorage } = finalVehicle;
    saveVehicle(vehicleForStorage);

    // Save all to Supabase in sequence: profile → garage → vehicle
    console.log('[PM-Onboarding] finish — user:', user ? user.id : 'NULL', 'isFirstTime:', isFirstTime);
    if (user) {
      try {
        // 1. Create/verify user profile (handles duplicate gracefully via upsert fallback)
        if (isFirstTime) {
          await createUserProfile(profileData, user.id, user.email || profileData.secondaryEmail || '');
          console.log('[PM-Onboarding] Supabase: user profile created/verified');

          // Link the secondary contact method (phone or email) so user can sign in with either
          try {
            if (profileData.phone) {
              await linkPhone(normalizePhone(profileData.phone));
              console.log('[PM-Onboarding] Linked phone to auth account');
            }
            if (profileData.secondaryEmail) {
              await linkEmail(profileData.secondaryEmail.trim().toLowerCase());
              console.log('[PM-Onboarding] Linked email to auth account');
            }
          } catch (linkErr) {
            console.error('[PM-Onboarding] Contact link failed (non-blocking):', linkErr.message);
          }
        }

        // 2. Ensure garage exists in Supabase
        let supaGarageId = null;
        if (!targetGarageId) {
          // Check if user already has a garage in Supabase (handles re-onboarding)
          const existingGarages = await loadUserGarages(user.id);
          if (existingGarages.length > 0) {
            supaGarageId = existingGarages[0].id;
            console.log('[PM-Onboarding] Supabase: using existing garage', supaGarageId);
          } else if (isFirstTime) {
            const supaGarage = await supaCreateGarage(user.id);
            supaGarageId = supaGarage.id;
            console.log('[PM-Onboarding] Supabase: garage created', supaGarageId);
          }
        }

        // 3. Create vehicle — use Supabase garage ID (not localStorage ID)
        const vehicleGarageId = supaGarageId || targetGarageId || null;
        const supaVehicle = await createVehicle(vehicleData, user.id, vehicleGarageId);
        console.log('[PM-Onboarding] Supabase: vehicle created', supaVehicle?.id, 'with garage:', vehicleGarageId);

        // Re-key local vehicle ID to Supabase UUID so everything stays in sync
        if (supaVehicle?.id && supaVehicle.id !== vehicleId) {
          const allV = getVehicles();
          const localMatch = allV.find((v) => v.id === vehicleId);
          if (localMatch) {
            localMatch.id = supaVehicle.id;
            // Also update garageId to the Supabase UUID if we got one
            if (supaGarageId) localMatch.garageId = supaGarageId;
            saveVehicles(allV);
            // Update finalVehicle so Processing uses the correct ID
            finalVehicle.id = supaVehicle.id;
            if (supaGarageId) finalVehicle.garageId = supaGarageId;
            console.log('[PM-Onboarding] Re-keyed vehicle ID:', vehicleId, '→', supaVehicle.id);
          }
        }

        // Also re-key garage in localStorage if we now have a Supabase UUID
        if (supaGarageId && garageId !== supaGarageId) {
          const allG = getGarages();
          const localGMatch = allG.find((g) => g.id === garageId);
          if (localGMatch) {
            localGMatch.id = supaGarageId;
            // Also update all vehicles referencing the old garage ID
            const allV2 = getVehicles();
            allV2.forEach((v) => {
              if (v.garageId === garageId) v.garageId = supaGarageId;
            });
            saveVehicles(allV2);
            saveGarages(allG);
            console.log('[PM-Onboarding] Re-keyed garage ID:', garageId, '→', supaGarageId);
          }
        }
      } catch (err) {
        // Log in all environments so we can debug onboarding failures
        console.error('[PM-Onboarding] Supabase save failed:', err);
        // Queue the vehicle creation for retry if it failed
        reliableWrite('vehicle', {
          userId: user.id,
          vehicleData,
          garageId: targetGarageId || null,
        }, () => createVehicle(vehicleData, user.id, targetGarageId || null));
      }
    } else {
      console.warn('[PM-Onboarding] finish — no auth user, skipping Supabase save');
    }

    onComplete(finalVehicle);
  }

  // Determine which screen to show
  // First-time: step 1=carousel, 2=account, 3=location, 4=optional, 5-7=vehicle flow
  // Returning: step 1-3=vehicle flow
  const vehicleStep = isFirstTime ? step - PROFILE_STEPS : step;
  const isVehiclePhase = isFirstTime ? step > PROFILE_STEPS : true;

  return (
    <>
      {/* Screen 1: Carousel (first-time only, no wrapper) */}
      {isFirstTime && step === 1 && (
        <StepCarousel onNext={next} />
      )}

      {/* Screens 2-4: Profile steps (first-time only) */}
      {isFirstTime && step === 2 && (
        <StepAccount data={profileData} update={updateProfile} onNext={next} onBack={back} user={user} />
      )}
      {isFirstTime && step === 3 && (
        <StepLocation data={profileData} update={updateProfile} onNext={next} onBack={back} />
      )}
      {isFirstTime && step === 4 && (
        <StepOptionalProfile data={profileData} update={updateProfile} onNext={next} onBack={back} />
      )}

      {/* Vehicle sub-flow (screens 5-7 for first-time, or 1-3 for returning) */}
      {isVehiclePhase && (
        <div className="screen">
          {/* Cancel button for returning users */}
          {onCancel && !isFirstTime && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
              <button
                onClick={onCancel}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0',
                  fontSize: '0.875rem', color: 'var(--text-secondary)',
                  background: 'none', border: 'none', cursor: 'pointer',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Cancel
              </button>
            </div>
          )}

          {/* Progress bar */}
          <div className="progress-bar">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={`progress-step ${
                  i + 1 === step ? 'active' : i + 1 < step ? 'done' : ''
                }`}
              />
            ))}
          </div>

          {/* Vehicle steps */}
          {vehicleStep === 1 && (
            <StepVehicle
              data={vehicleData}
              update={updateVehicle}
              onNext={next}
              isFirstTime={isFirstTime}
            />
          )}
          {vehicleStep === 2 && (
            <StepContext
              data={vehicleData}
              update={updateVehicle}
              onNext={next}
              onBack={back}
            />
          )}
          {vehicleStep === 3 && (
            <StepPhoto
              data={vehicleData}
              update={updateVehicle}
              onBack={back}
              onFinish={finish}
              submitting={submitting}
            />
          )}
        </div>
      )}
    </>
  );
}
